import { CommonModule } from '@angular/common'
import { Component } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { Router, RouterModule } from '@angular/router'
import { NavFavoritesService } from '@employer/app/services/nav-favorites.service'
import { GatedSettingsCategory, SettingsGroup } from '@employer/app/services/settings-catalog'
import { SettingsSearchService } from '@employer/app/services/settings-search.service'
import { E11InputModule } from '@engineering11/ui-lib/e11-input'
import { LetDirective } from '@ngrx/component'
import { TranslateModule } from '@ngx-translate/core'
import { map } from 'rxjs'

interface SettingsRailGroup {
  group: SettingsGroup
  categories: GatedSettingsCategory[]
}

interface SettingsRailViewModel {
  groups: SettingsRailGroup[]
  total: number
}

/**
 * Render order for the grouped rail; groups with no visible categories are dropped.
 * CNCT-6779: these headings are now the only place the hub shows its grouping — the
 * cards in the overview grid carry a description instead of a group label.
 */
const GROUP_ORDER: SettingsGroup[] = ['Company', 'Website', 'People', 'Openings', 'Pipelines', 'Settings']

/** Delay after navigation before scrolling to an in-page section anchor (mirrors menu-left/report-filtering-nav). */
const SECTION_SCROLL_DELAY_MS = 250

@Component({
  selector: 'settings-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, TranslateModule, E11InputModule, LetDirective],
  templateUrl: './settings-nav.component.html',
  styles: [
    `
      /* Manage Openings' search box (the --mc-search-* variables in styles/_modern-components.scss).
         e11-input's own styles are attribute-encapsulated in the library, so this reaches the
         <input> via ::ng-deep. */
      :host ::ng-deep e11-input .e11-input-text input:not(.e11-rounded-full) {
        height: var(--mc-search-height);
        min-height: var(--mc-search-height);
        max-height: var(--mc-search-height);
        border-radius: var(--mc-search-radius);
        border-color: var(--mc-search-border);
        font-size: var(--mc-search-font-size);
      }
      :host ::ng-deep e11-input .e11-input-text input:not(.e11-rounded-full):hover,
      :host ::ng-deep e11-input .e11-input-text input:not(.e11-rounded-full):focus {
        border-color: var(--mc-search-border-hover);
      }
      :host ::ng-deep e11-input .e11-input-text input:not(.e11-rounded-full)::placeholder {
        color: var(--mc-search-placeholder);
      }

      /* Material Symbols expresses fill as an axis, so a "filled" star is the same
         glyph with FILL 1 rather than a different ligature. */
      .star-filled {
        font-variation-settings: 'FILL' 1;
      }

      /* Rows take the button corners (--mc-button-radius in styles/_modern-components.scss), not
         e11-rounded's 4px, so the active row reads like the app's buttons. */
      .rail-row {
        border-radius: var(--mc-button-radius);
      }

      /* Hover-revealed, matching the left nav's own stars — a always-visible column
         of outlines would read as clutter in a 288px rail. */
      .rail-star {
        color: rgba(var(--color-app-borders), 1);
        opacity: 0;
        transition: opacity 0.15s ease, color 0.15s ease;
      }
      .rail-row:hover .rail-star,
      .rail-star.rail-star-on,
      .rail-star:focus-visible {
        opacity: 1;
      }
      .rail-star:hover,
      .rail-star.rail-star-on {
        color: rgba(var(--color-primary-accent), 1);
      }
      /* On the selected row the accent fill is behind the star, so it goes white. */
      .rail-row-active .rail-star,
      .rail-row-active .rail-star.rail-star-on {
        color: rgba(var(--color-text-white), 1);
        opacity: 1;
      }

      /* The row's hover rule sets background-color outright rather than layering, so
         hovering the SELECTED row would swap its accent fill for a 10% grey and leave
         white text on a near-white ground. Keep the fill and darken it instead, so the
         row stays recognisably selected and the text stays white throughout.
         The first declaration is the fallback for engines without color-mix; the second
         wins where it is supported and is a true darkening of the fill itself. */
      .rail-row.rail-row-active:hover {
        background-color: rgba(var(--color-blue), 1);
        background-color: color-mix(in srgb, rgba(var(--color-primary-accent), 1) 85%, #000);
        color: rgba(var(--color-text-white), 1);
      }
      .rail-row.rail-row-active:hover .rail-star,
      .rail-row.rail-row-active:hover .rail-star.rail-star-on,
      .rail-row.rail-row-active:hover .rail-star:hover {
        color: rgba(var(--color-text-white), 1);
      }

      @media (prefers-reduced-motion: reduce) {
        .rail-star {
          transition: none;
        }
      }
    `,
  ],
})
export class SettingsNavComponent {
  searchControl = new FormControl<string>('', { nonNullable: true })

  vm$ = this.searchService
    .search(this.searchControl.valueChanges.pipe(map(value => value ?? '')))
    .pipe(map(categories => ({ groups: this.groupByGroup(categories), total: categories.length } as SettingsRailViewModel)))

  readonly favorites$ = this.navFavorites.favorites$

  constructor(private searchService: SettingsSearchService, private router: Router, private navFavorites: NavFavoritesService) {}

  onSelect(category: GatedSettingsCategory): void {
    this.router.navigate([category.route]).then(() => {
      // Section-jump applies only to categories with a verified anchor (Task 13); all others are page-level.
      if (category.section) {
        setTimeout(() => this.scrollToSection(category.section!), SECTION_SCROLL_DELAY_MS)
      }
    })
  }

  /** Read off the one `favorites` list the template resolves, so no per-row subscription. */
  isFavorite(favorites: string[] | null, category: GatedSettingsCategory): boolean {
    return !!favorites?.includes(category.favoriteId)
  }

  /** The star sits inside the row's anchor — keep the click off the navigation. */
  onToggleFavorite(event: Event, category: GatedSettingsCategory): void {
    event.preventDefault()
    event.stopPropagation()
    this.navFavorites.toggleFavorite(category.favoriteId)
  }

  trackByCategory(_index: number, category: GatedSettingsCategory): string {
    return category.id
  }

  trackByGroup(_index: number, group: SettingsRailGroup): string {
    return group.group
  }

  private scrollToSection(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  private groupByGroup(categories: GatedSettingsCategory[]): SettingsRailGroup[] {
    const byGroup = new Map<SettingsGroup, GatedSettingsCategory[]>()
    for (const category of categories) {
      const existing = byGroup.get(category.group)
      if (existing) existing.push(category)
      else byGroup.set(category.group, [category])
    }
    return GROUP_ORDER.filter(group => byGroup.has(group)).map(group => ({ group, categories: byGroup.get(group)! }))
  }
}
